/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useCallback, useEffect, useState } from "react";
import type { ComponentProps, ChangeEvent } from "react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type {
  SetFormTemplateRequest,
  GetFormTemplateResponse,
  SetFormTemplateResponse,
} from "@ServerTypes/projects/project.schema";
import { Trans, useTranslation } from "@i18n";
import { untranslated, type TranslatedString } from "@i18n";
import axiosWrapper from "@AxiosWrapper";
import { TemplateUrl } from "@Services/dataManager";
import { formCategories } from "@Helpers";
import { RecursiveConfirmationPopup } from "@Features/files";
import { showToastNotification } from "@Reducers/toastSlice";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import _ from "lodash";
import IconComponent from "@CubeComponents/icons";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import FormTemplatePreviewPopup from "./FormTemplatePreviewPopup";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import type { StandardResponse } from "@Types/fileOperations";
import type { SetFileModelFormTemplateIdRequest } from "@ServerTypes/fileOperations/fileOperations.types.js";
import type { SetAssetFormTemplateResponse } from "@ServerTypes/assets/assets.schema";

/** One selectable template in the picker, from `getTemplates`. */
type FormTemplateOption = { _id?: string; name?: string; isDefault?: boolean };

interface FormTemplateProps {
  /** Present only on the file-details surface; the settings surfaces render without one. */
  selectedFile?: {
    hierarchyId?: string;
    modelFormTemplateId?: string;
    resourceType?: string;
    permission?: { write?: boolean };
  };
  projectId: ObjectIdString;
  /** A key of `formCategories` / `resourceTypeText` ("files", "issues", "model", …). */
  resourceType: string;
  /** DEAD PROP: passed by `FileVersion` but read nowhere here. */
  engineType?: string;
}

function FormTemplate({ selectedFile, projectId, resourceType }: FormTemplateProps) {
  const resourceTypeText = {
    files: "Files",
    issues: "Issues",
    mailBox: "Mails",
    model: "Models",
    transmittal: "Transmittals",
    assets: "ASSETS",
    assetsInstance: "ASSETS_INSTANCE",
    drawing: "drawing",
  };

  const defaultRecursiveConfirmationPopupInfo = {
    visibilityStatus: "closed",
    radioButtonLabels: [] as string[],
    headerTitle: "",
    // Holds a plain string, a translated string, or a `<Trans>` element depending on the branch.
    additionalTextBeforeOptions: "" as React.ReactNode,
    optionButtonsText: [] as string[],
    action: "update",
    recursiveOverrideWarningMessage: "" as React.ReactNode,
    isRecursive: false,
    potentialFormTemplateOptionId: undefined as string | undefined,
    potentialFormTemplateOptionName: undefined as string | undefined,
  };

  const defaultSelectedFormTemplateInfo = {
    // `undefined` too: both update paths assign the incoming template id, which is optional.
    id: null as string | null | undefined,
    isPreviewModalOpen: false,
    isDisabled: false,
    isVaries: false,
    tooltipText: "",
  };

  const { t } = useTranslation();
  const [selectedFormTemplateInfo, setSelectedFormTemplateInfo] = useState(defaultSelectedFormTemplateInfo);
  const [recursiveConfirmationPopupInfo, setRecursiveConfirmationPopupInfo] = useState(
    defaultRecursiveConfirmationPopupInfo,
  );
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  const dispatch = useDispatch();

  const query = {
    resourceType: (formCategories as Record<string, string>)[resourceType]!,
    isArchived: false,
    params: {
      resourceType: (formCategories as Record<string, string>)[resourceType]!,
      isArchived: false,
    },
  };

  const init = async () => {
    const result = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplateId`, {
      projectId,
      resourceType: (formCategories as Record<string, string>)[resourceType]!,
      hierarchyId: selectedFile?.hierarchyId,
    });
    const selectedFormTempId = _.get(result, "data.data.formTemplateId", null);

    setSelectedFormTemplateInfo({
      ...defaultSelectedFormTemplateInfo,
      id: selectedFormTempId,
      // `hierarchyId && !write` yields `string | boolean`, not `boolean`; only its truthiness is
      // used downstream. Erased assertion, preserved verbatim.
      isDisabled: (selectedFile?.resourceType == "RESOURCE_TYPE_MERGED_FILE" ||
        (selectedFile?.hierarchyId && !selectedFile?.permission?.write)) as boolean,
      isVaries: _.get(result, "data.data.isVaries", false),
      tooltipText: _.get(result, "data.data.tooltipText", ""),
    });
    setShowOverrideWarning(!["issues", "model", "files", "assets"].includes(resourceType));
  };

  useEffect(() => {
    if (selectedFile?.modelFormTemplateId) {
      setSelectedFormTemplateInfo({
        ...defaultSelectedFormTemplateInfo,
        id: selectedFile.modelFormTemplateId,
      });
    } else {
      resourceType && projectId && init();
    }
  }, [resourceType, projectId, selectedFile]);

  const textLabel = {
    model: "Model",
    drawing: "Drawing",
    assets: "Assets",
    issues: "Issues",
    mailBox: "Mails",
  };

  const handleTemplateChange = async (e: FormTemplateOption) => {
    const potentialFormTemplateOptionId = e._id;
    const potentialFormTemplateOptionName = e.name;
    const recursiveOverrideWarningMessage = (
      <Trans
        i18nKey="override_warning_for_templ_change"
        values={{
          resource_type: (resourceTypeText as Record<string, string>)[resourceType],
        }}
      />
    );
    if (resourceType === "assetsInstance" || resourceType === "assets") {
      if (selectedFormTemplateInfo.id) {
        setRecursiveConfirmationPopupInfo({
          ...defaultRecursiveConfirmationPopupInfo,
          radioButtonLabels: [
            t("Override custom fields and data in existing Assets"),
            t(
              "Retain custom fields and data in existing Assets and new Assets will contain custom fields from the selected template",
            ),
          ],
          headerTitle: t("Change Template"),
          additionalTextBeforeOptions: (
            <>
              {t("Are you sure you want to set")} <strong>{potentialFormTemplateOptionName}</strong>{" "}
              {t("as template for Assets?")}
            </>
          ),
          optionButtonsText: [t("Save"), t("Cancel")],
          visibilityStatus: "open",
          potentialFormTemplateOptionId,
          potentialFormTemplateOptionName,
          recursiveOverrideWarningMessage,
        });
      } else {
        await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
        if (resourceType === "assetsInstance") {
          await axiosWrapper.post<SetAssetFormTemplateResponse>(`${getBackendUrl()}/assets/setAssetFormTemplate`, {
            projectId,
          });
        }
      }
    } else if (["model", "drawing"].includes(resourceType)) {
      if (selectedFormTemplateInfo.id) {
        setRecursiveConfirmationPopupInfo({
          ...defaultRecursiveConfirmationPopupInfo,
          visibilityStatus: "open",
          radioButtonLabels: [t("Yes"), t("No")],
          headerTitle: t("Change Template"),
          additionalTextBeforeOptions: selectedFile
            ? `Do you want to override the ${textLabel[resourceType as keyof typeof textLabel]} template for this ${textLabel[resourceType as keyof typeof textLabel]}?`
            : `Do you want to override the ${textLabel[resourceType as keyof typeof textLabel]} template for all files in this project?`,
          potentialFormTemplateOptionId,
          potentialFormTemplateOptionName,
          recursiveOverrideWarningMessage,
        });
      } else {
        await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
      }
    } else if (resourceType === "files") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else if (resourceType === "issues") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        radioButtonLabels: [
          t("Override custom fields and data in existing issues"),
          t(
            "Retain custom fields and data in existing issues and new issues will contain custom fields from the selected template",
          ),
        ],
        headerTitle: t("Set Custom Fields Template"),
        additionalTextBeforeOptions: (
          <Trans
            i18nKey="set_this_custom_template_confirmation_question"
            values={{
              custom_form_template_name: potentialFormTemplateOptionName,
            }}
            components={{ bold: <strong /> }}
          />
        ),
        optionButtonsText: [t("Apply"), t("Cancel")],
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else if (resourceType === "mailBox") {
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        radioButtonLabels: [
          t("Override custom fields and data in existing mails"),
          t(
            "Retain custom fields and data in existing mails and new mails will contain custom fields from the selected template",
          ),
        ],
        headerTitle: t("Set Custom Fields Template"),
        additionalTextBeforeOptions: (
          <Trans
            i18nKey="set_this_custom_template_confirmation_question"
            values={{
              custom_form_template_name: potentialFormTemplateOptionName,
            }}
            components={{ bold: <strong /> }}
          />
        ),
        optionButtonsText: [t("Apply"), t("Cancel")],
        visibilityStatus: "open",
        potentialFormTemplateOptionId,
        potentialFormTemplateOptionName,
        recursiveOverrideWarningMessage,
      });
    } else {
      await updateFormTemplate(potentialFormTemplateOptionId, potentialFormTemplateOptionName);
      setRecursiveConfirmationPopupInfo({
        ...defaultRecursiveConfirmationPopupInfo,
        visibilityStatus: "closed",
      });
    }
  };

  const handlePopupClose = () => {
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      visibilityStatus: "closedFromCancel",
    });
  };

  const toggleIsRecursive = (event: ChangeEvent<HTMLInputElement>) => {
    setRecursiveConfirmationPopupInfo((prev) => ({
      ...prev,
      isRecursive: event.target.value === "overRide",
    }));
  };

  const updateFileModelTemplate = async (templateId?: string, templateName?: string) => {
    const payload: SetFileModelFormTemplateIdRequest = {
      // Only reachable from the file-details surface, which always supplies both. Erased.
      fileId: selectedFile!.hierarchyId!,
      templateId: templateId!,
      projectId,
      isRecursive: recursiveConfirmationPopupInfo.isRecursive,
    };
    const response: { data: StandardResponse } = await axiosWrapper.post<StandardResponse>(
      `${getBackendUrl()}/fileOperations/setFileModelFormTemplateId`,
      payload,
    );
    if (response.data.status) {
      setSelectedFormTemplateInfo({
        ...selectedFormTemplateInfo,
        id: templateId,
      });
      dispatch(
        showToastNotification({
          content: t("success_message_for_template_change", { template_name: templateName }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage
            ? untranslated(response.data.errorMessage)
            : t("Template Change Failed !"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const updateFormTemplate = async (
    potentialFormTemplateOptionId?: string,
    potentialFormTemplateOptionName?: string,
  ) => {
    const action = recursiveConfirmationPopupInfo.action;
    // `id` is added only for the non-reset actions, so the payload is built incrementally.
    const sendObject: SetFormTemplateRequest & { action?: string } = {
      projectId,
      resourceType: (formCategories as Record<string, string>)[resourceType]!,
      isRecursive: recursiveConfirmationPopupInfo.isRecursive,
      action,
    };
    if (action !== "reset") {
      sendObject.id = potentialFormTemplateOptionId as ObjectIdString;
    }
    let formTemplateUpdateResult = await axiosWrapper.post<SetFormTemplateResponse>(
      `${getBackendUrl()}/projects/setFormTemplate`,
      sendObject,
    );
    if (_.get(formTemplateUpdateResult, "data.status")) {
      let successMessage;
      if (action == "update") {
        setSelectedFormTemplateInfo({
          ...defaultSelectedFormTemplateInfo,
          id: potentialFormTemplateOptionId,
        });
        successMessage = t("success_message_for_template_change", { template_name: potentialFormTemplateOptionName });
      } else if (action == "reset") {
        setSelectedFormTemplateInfo(defaultSelectedFormTemplateInfo);
        successMessage = t("success_message_for_template_reset", {
          resource_type: (resourceTypeText as Record<string, string>)[resourceType],
        });
      } else {
        successMessage = t("Template updated successfully");
      }
      dispatch(
        showToastNotification({
          content: successMessage,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      const errorMessage = _.get(
        formTemplateUpdateResult,
        "data.errorMessage",
        t("Template Change Failed !"),
      ) as TranslatedString;
      dispatch(
        showToastNotification({
          content: errorMessage,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleRecursiveConfirmation = async () => {
    if (selectedFile?.hierarchyId) {
      await updateFileModelTemplate(
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionId,
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionName,
      );
    } else {
      await updateFormTemplate(
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionId,
        recursiveConfirmationPopupInfo.potentialFormTemplateOptionName,
      );
      if (resourceType === "assets") {
        await axiosWrapper.post<SetAssetFormTemplateResponse>(`${getBackendUrl()}/assets/setAssetFormTemplate`, {
          projectId,
        });
      }
    }
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      visibilityStatus: "closedAfterSumbit",
    });
  };

  const handleResetTemplate = async () => {
    const resourceLabel = (textLabel as Record<string, string>)[resourceType];
    setRecursiveConfirmationPopupInfo({
      ...defaultRecursiveConfirmationPopupInfo,
      radioButtonLabels: [
        // `resourceType` is always one of `textLabel`'s keys on the surfaces that render this.
        t("remove_custom_fields_template_reset", { resource_type: resourceLabel!.toLowerCase() }),
        t("retain_custom_fields_template_reset", { resource_type: resourceLabel!.toLowerCase() }),
      ],
      headerTitle: t("Reset Custom Fields Template"),
      additionalTextBeforeOptions: t("Are you sure you want to reset custom fields Template?"),
      optionButtonsText: [t("Apply"), t("Cancel")],
      visibilityStatus: "open",
      action: "reset",
      recursiveOverrideWarningMessage: (
        <Trans
          i18nKey="override_warning_for_templ_reset"
          values={{
            resource_type: (resourceTypeText as Record<string, string>)[resourceType],
          }}
        />
      ),
    });
  };

  const renderTemplateOption = useCallback(
    (option: FormTemplateOption) => (
      <div className="flex items-center gap-2 w-full overflow-hidden">
        <span className="truncate">{option.name}</span>
        {option.isDefault && (
          <span className="rounded-full bg-gray-500 text-white px-2 py-1 shrink-0">{t("Default")}</span>
        )}
      </div>
    ),
    [t],
  );

  const renderDropDown = useCallback(() => {
    const showTooltip = selectedFormTemplateInfo.isVaries;
    const tooltipText = selectedFormTemplateInfo.tooltipText;
    const dropdown = (
      <SelectDropDown
        disabled={selectedFormTemplateInfo.isDisabled}
        remoteDataSource={TemplateUrl}
        query={query}
        searchable
        // DECLARATION GAP in `SelectDropDown`: with `fields` naming a value field it resolves a bare
        // id string against its options at runtime, but its `value` prop is declared `T | T[] | null`
        // and never admits the id form. Asserted through an intersection including the source type;
        // `onChange` likewise is declared over the union it never actually emits here.
        value={selectedFormTemplateInfo.id as typeof selectedFormTemplateInfo.id & FormTemplateOption}
        fields={{ value: "_id", text: "name" }}
        onChange={handleTemplateChange as ComponentProps<typeof SelectDropDown>["onChange"]}
        width={"100%"}
        placeholder={selectedFormTemplateInfo.isVaries ? t("Varies") : t("Select a Template")}
        renderOption={renderTemplateOption}
      />
    );
    return (
      <div className="flex items-center w-full gap-8">
        <label className="font-semibold whitespace-nowrap">
          {selectedFile
            ? resourceType == "model"
              ? t("Model Template")
              : t("Drawing Template")
            : t("Select a Form Template")}
        </label>
        {showTooltip ? <ToolTipComponent content={untranslated(tooltipText)}>{dropdown}</ToolTipComponent> : dropdown}
      </div>
    );
  }, [selectedFile, selectedFormTemplateInfo.id, selectedFormTemplateInfo.isVaries, renderTemplateOption]);

  return (
    <div className={`h-full w-full flex flex-col gap-8 ${selectedFile ? "p-2" : "p-8"}`}>
      {recursiveConfirmationPopupInfo.visibilityStatus === "open" && (
        <RecursiveConfirmationPopup
          handleSubmit={handleRecursiveConfirmation}
          handleClose={handlePopupClose}
          module="project"
          header="form template"
          toggleIsRecursive={toggleIsRecursive}
          radioButtonLabels={recursiveConfirmationPopupInfo.radioButtonLabels}
          headerTitle={recursiveConfirmationPopupInfo.headerTitle}
          additionalTextBeforeOptions={recursiveConfirmationPopupInfo.additionalTextBeforeOptions}
          optionButtonsText={recursiveConfirmationPopupInfo.optionButtonsText}
          isRecursive={recursiveConfirmationPopupInfo.isRecursive}
          recursiveOverrideWarningMessage={recursiveConfirmationPopupInfo.recursiveOverrideWarningMessage}
          engineType
        />
      )}
      <div className="flex w-full items-center gap-4">
        {renderDropDown()}
        {["assets", "issues", "mailBox"].includes(resourceType) && (
          <div className="flex items-center gap-4">
            {["assets"].includes(resourceType) && (
              <ButtonComponent onClick={handleResetTemplate} disabled={!selectedFormTemplateInfo.id}>
                <div className="flex items-center gap-2">
                  <IconComponent
                    name="reset_template"
                    width="1rem"
                    height="1rem"
                    isEnabled={!!selectedFormTemplateInfo.id}
                  />
                  <Text>{t("Reset")}</Text>
                </div>
              </ButtonComponent>
            )}
            <ButtonComponent
              disabled={!selectedFormTemplateInfo.id}
              onClick={() => {
                setSelectedFormTemplateInfo((prev) => ({ ...prev, isPreviewModalOpen: true }));
              }}
            >
              <Text>{t("Preview")}</Text>
            </ButtonComponent>
          </div>
        )}
      </div>

      {showOverrideWarning && (
        <p className="text-sm">
          <Text>
            <Trans
              i18nKey="template_change_message"
              values={{
                resource_type:
                  resourceType == "assets" ? "Assets" : (resourceTypeText as Record<string, string>)[resourceType],
              }}
            />
          </Text>
        </p>
      )}
      <FormTemplatePreviewPopup
        isVisible={selectedFormTemplateInfo.isPreviewModalOpen}
        onCancel={() => {
          setSelectedFormTemplateInfo((prev) => ({
            ...prev,
            isPreviewModalOpen: false,
          }));
        }}
        formTemplateId={selectedFormTemplateInfo.id}
        projectId={projectId}
        resourceType={resourceType}
      />
    </div>
  );
}

export default FormTemplate;
